import { CtaStrip, FeatureList, MarketingPage, Pillars } from '../../components/MarketingPage';

/** /features/ deep-dive pages */

export function LienLawCompliance() {
  return (
    <MarketingPage
      seoTitle="Lien-law compliance engine — AuctiSync Features"
      seoDescription="State-specific lien-law compliance engine for storage, auto-repair, RV, tow, marina, and pawn-shop verticals. Rule engine, certified notice delivery, audit-grade output."
      canonicalPath="/features/lien-law-compliance"
      eyebrow="Feature"
      headline="Every state's lien laws, encoded as an engine."
      subhead="The compliance workflow the incumbents refused to build. State-by-state delinquency timers, statutory notice language, certified delivery, advertisement placement, redemption tracking, excess proceeds — one engine across every lien-law vertical."
    >
      <Pillars
        title="What the engine actually does"
        items={[
          {
            title: 'State rule configuration',
            body: 'Each state has its own delinquency trigger, pre-lien wait, final notice content, advertisement window, sale window. We encode all of it.',
          },
          {
            title: 'Statutory notice templates',
            body: 'Templates with state-required language. Override per tenant, per facility — but the statutory baseline is built in.',
          },
          {
            title: 'Multi-channel delivery',
            body: 'RPost certified email + USPS Certified Mail API + SMS + in-app. Pick what your state requires; we do the rest.',
          },
          {
            title: 'Advertisement placement',
            body: 'Column.us API handles the newspaper advertisement most states require. Schedule, verify, archive the tear sheet.',
          },
          {
            title: 'Auto-compliance gate',
            body: 'A listing cannot publish until every state-required timer clears. You literally cannot publish non-compliant.',
          },
          {
            title: 'Redemption + void',
            body: 'Tenant pays late? The sale auto-voids with a clean audit trail. Never oversell a redeemed unit.',
          },
          {
            title: 'Excess proceeds accounting',
            body: 'State-specific excess proceeds calculation, tenant notification, escrow handling. Audit-grade.',
          },
          {
            title: 'Per-sale PDF audit',
            body: 'Every notice, every delivery proof, full timeline, sale price, buyer info, chain-of-custody — one PDF per sale.',
          },
        ]}
      />

      <FeatureList
        title="Verticals the engine already covers"
        body="Same engine, different statutory frameworks."
        bullets={[
          'Storage units (FL/TX/AZ/GA/NC Phase 1, expanding)',
          "Auto repair mechanic's lien (Phase 1.5)",
          'RV + camper abandonment (Phase 1.5)',
          'Tow-yard abandoned vehicles (Phase 2)',
          'Trucking freight liens (Phase 2)',
          'Pawn-shop lien sales (Phase 2)',
          'Marina / boat liens (Phase 3)',
          'Innkeeper liens (Phase 3)',
        ]}
      />

      <CtaStrip
        headline="Stop managing lien calendars in a spreadsheet."
        subhead="The engine is included on the Storage tier and every vertical we launch."
        primaryLabel="See Storage tier pricing"
        primaryHref="/pricing"
        secondaryLabel="See pricing"
        secondaryHref="/pricing"
      />
    </MarketingPage>
  );
}

export function BiddingEngine() {
  return (
    <MarketingPage
      seoTitle="Atomic bidding engine — AuctiSync Features"
      seoDescription="The atomic place-bid RPC that powers AuctiSync — PostgreSQL-native, sub-200ms, load-tested to 100 concurrent bids on the same lot. No double-acceptance, no race conditions, no drama."
      canonicalPath="/features/bidding-engine"
      eyebrow="Feature"
      headline="The bidding engine your buyers won't even notice."
      subhead="That's the point. An atomic PostgreSQL RPC runs every bid. Sub-200ms round-trip. Load-tested to 100 concurrent bids on the same lot — all resolved correctly. No double-acceptance, no ghosted bids, no 'oops, refresh and try again.'"
    >
      <Pillars
        title="The boring details that make bidding feel magical"
        items={[
          {
            title: 'Atomic place_bid RPC',
            body: 'Every bid path — UI, mobile, live ring, proxy, embed widget — calls the same PostgreSQL RPC. No direct INSERT anywhere.',
          },
          {
            title: 'SELECT FOR UPDATE NOWAIT',
            body: 'Row-level locking with immediate fail-fast on contention. Concurrent bids queue cleanly; no timeout deadlocks.',
          },
          {
            title: 'Idempotency built in',
            body: 'Unique constraint on (bidder, lot, idempotency_key). Retry on network glitch is safe — duplicate is rejected, not double-charged.',
          },
          {
            title: 'Anti-snipe extension',
            body: 'Configurable threshold (default 3 min) and extension (default 3 min). Reset or extend modes. Server-authoritative timer.',
          },
          {
            title: 'Server time sync',
            body: 'All clients sync against `get_server_time()` RPC — never trust the browser clock. Extensions fire on server time.',
          },
          {
            title: 'Load-tested + proven',
            body: '25 concurrent bidders × 10 rounds on the same lot. Accepted bid count = bids table. Cache = source of truth. One winner per increment step. Zero duplicates.',
          },
        ]}
      />

      <FeatureList
        title="Integrity primitives ready for Trustee-Grade"
        body="Optional for now, trustees will pay for it later."
        bullets={[
          'Hash-chained bid log (each bid hashes the previous)',
          'Public-anchor option (periodic merkle root to Bitcoin/Ethereum)',
          'RFC 3161 timestamps (independent time authority)',
          'Append-only corrections (no silent edits — every change leaves a trail)',
          'Per-tenant toggle (opt-in for trustee + high-value customers)',
          'pgTAP RLS tests on every table',
          'Closed-source SKIP LOCKED cron for auction close',
          'Public uptime dashboard (coming Phase 1)',
        ]}
      />

      <CtaStrip
        headline="The engine runs. The bidders don't think about it. That's the goal."
        subhead="See how DTA's 10+ years of concurrent weekly bidding moved over cleanly."
        primaryLabel="Read the DTA case study"
        primaryHref="/case-studies/dta"
        secondaryLabel="See pricing"
        secondaryHref="/pricing"
      />
    </MarketingPage>
  );
}
